import AlternateEmail from "@mui/icons-material/AlternateEmail";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import LanOutlined from "@mui/icons-material/LanOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import SensorsOutlined from "@mui/icons-material/SensorsOutlined";
import Tag from "@mui/icons-material/Tag";
import { Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { CopyAction } from "../../../moment-card/components/actions/copy";
import { useArchiveMoment } from "../../../moment-card/hooks/use-archive-moment";
import { statusToPill } from "../../../moment-card/utils";
import { communicationAudience, communicationTiming } from "./communication-summary";
import { RecentlyEditedMenu } from "./recently-edited-menu";
import { RecentlyEditedRow, RowDetail } from "./recently-edited-row";

import type { RecentlyEditedItem } from "../../hooks/use-recently-edited";
import type { FC, ReactElement } from "react";

const audienceIcons: Record<ReturnType<typeof communicationAudience>["kind"], ReactElement> = {
  segment: <GroupsOutlined />,
  connection: <LanOutlined />,
  notify: <AlternateEmail />,
  channel: <Tag />,
  none: <HelpOutlineOutlined />,
};

const timingIcons: Record<ReturnType<typeof communicationTiming>["kind"], ReactElement> = {
  immediate: <SendOutlined />,
  scheduled: <CalendarMonthOutlined />,
  triggered: <SensorsOutlined />,
  "survey-completed": <FactCheckOutlined />,
  none: <HelpOutlineOutlined />,
};

/** A Communication in the feed: who it goes to, its program, and when it's sent. */
export const CommunicationRow: FC<{
  communication: RecentlyEditedItem;
  /** Takes it out of the feed once it's archived. */
  onArchived: () => void;
}> = ({ communication, onArchived }) => {
  const { archive, archiving } = useArchiveMoment(
    communication.id,
    communication.communication_status,
  );
  const audience = communicationAudience(communication);
  const timing = communicationTiming(communication);

  return (
    <RecentlyEditedRow
      media={
        communication.channel ? (
          <ChannelIcon channel={communication.channel} />
        ) : (
          <Typography variant="subtitle2" sx={{ color: "text.secondary", textAlign: "center" }}>
            No channel
          </Typography>
        )
      }
      title={communication.title}
      href={
        communication.follow_up
          ? `/follow-up/${communication.follow_up.id}`
          : `/moments/${communication.id}`
      }
      analyticsId="dashboard-recently-edited-communication"
      pill={communication.communication_status && statusToPill(communication.communication_status)}
      // Clonable regardless of status: the copy page still turns away a Communication that can't be
      // copied. Only one without a Program is stopped here.
      action={
        <>
          <CopyAction
            id={communication.id}
            title={communication.title}
            clonable
            disabled={
              communication.program
                ? undefined
                : { reason: "This Communication must have a Program selected before copying" }
            }
          />
          <RecentlyEditedMenu
            title={communication.title}
            label="Communication"
            archive={() => archive(true)}
            archiving={archiving}
            cannotArchive={
              communication.communication_status === "active"
                ? "Archive is unavailable for active Communications. Please pause the Communication first."
                : undefined
            }
            onArchived={onArchived}
          />
        </>
      }
      details={
        <>
          <RowDetail icon={audienceIcons[audience.kind]}>{audience.label}</RowDetail>
          <RowDetail>
            {communication.program ? (
              <Link
                component={RouterLink}
                to={`/tabbed-programs/${communication.program.id}`}
                underline="hover"
                data-analytics-id="dashboard-recently-edited-program"
              >
                {communication.program.title}
              </Link>
            ) : (
              "Program not defined"
            )}
          </RowDetail>
          <RowDetail icon={timingIcons[timing.kind]}>
            <span>{timing.content}</span>
          </RowDetail>
        </>
      }
    />
  );
};
